import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { OrganisationSettingsService } from '@/api/services/OrganisationSettingsService';
// Deep-imported so a `vi.mock('@/api')` cannot leave the enum object undefined.
import { organisations_UpdateAbsenceSettingsRequest } from '@/api/models/organisations_UpdateAbsenceSettingsRequest';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';
import type { AbsenceSettings, AbsenceSettingsInput } from '@/types/employeeAbsences';

// Multi-tenant query keys include the org id so a tenant switch never serves
// another org's cached settings.
export const absenceSettingsKeys = {
  all: ['absence-settings'] as const,
  settings: (orgId: string) => [...absenceSettingsKeys.all, orgId] as const,
};

/**
 * `AbsenceSettingsInput['certificateMode']` is a string-literal union while the
 * generated DTO uses a nominal enum with the SAME two values. The mapping below
 * substitutes the member exhaustively: no cast, no runtime change.
 */
function toUpdateAbsenceSettingsRequest(
  input: AbsenceSettingsInput,
): organisations_UpdateAbsenceSettingsRequest {
  return {
    certificateMode:
      input.certificateMode === 'EAU'
        ? organisations_UpdateAbsenceSettingsRequest.certificateMode.EAU
        : organisations_UpdateAbsenceSettingsRequest.certificateMode.UPLOAD,
    certificateDueDay: {
      SICK: input.certificateDueDay.SICK,
      ACCIDENT: input.certificateDueDay.ACCIDENT,
      CHILD_SICK: input.certificateDueDay.CHILD_SICK,
    },
    certificateRetentionMonths: input.certificateRetentionMonths,
  };
}

interface UseAbsenceSettingsOptions {
  /**
   * Extra gate on top of the auth gate, e.g. a page that only needs the
   * settings for one view. Defaults to true.
   */
  enabled?: boolean;
}

/**
 * Read the organisation's absence settings (certificate mode, due day per
 * report type, retention). The backend always answers the full resolved shape,
 * defaults included.
 */
export function useAbsenceSettings(orgId: string, options?: UseAbsenceSettingsOptions) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: absenceSettingsKeys.settings(orgId),
    queryFn: async (): Promise<AbsenceSettings> =>
      OrganisationSettingsService.getApiV1OrganisationsAbsenceSettings(orgId),
    enabled: !!isSignedIn && isAuthReady && !!orgId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });
}

/** Persist the absence settings (admin only; the backend answers 403 otherwise). */
export function useUpdateAbsenceSettings() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({
      orgId,
      settings,
    }: {
      orgId: string;
      settings: AbsenceSettingsInput;
    }): Promise<AbsenceSettings> =>
      OrganisationSettingsService.putApiV1OrganisationsAbsenceSettings(
        orgId,
        toUpdateAbsenceSettingsRequest(settings),
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: absenceSettingsKeys.settings(variables.orgId),
      });
      toast.success(
        i18n._(msg({ id: 'settings.absences.toast.saved', message: 'Absence settings saved' })),
      );
    },
    onError: (error: unknown) => {
      toast.error(
        translateBackendError(
          extractBackendError(error).code,
          i18n,
          msg({ id: 'common.error.updateFailed', message: 'Failed to update' }),
        ),
      );
    },
  });
}
